// Self-describing dashboard widgets: the client-side mirror of the closed
// `core` metrics registry (which dimensions/filters/buckets a metric admits —
// write truth stays server-side; this mirror only shapes the editor), the
// closed visualization vocabulary, the per-(metric, visualization) rendering transforms from the
// `series[] -> groups[] -> points[]` envelope, and the Overview starter
// template. Pure — no React, no network — so everything here is unit-testable
// and the tile/editor components stay thin.

import type { components } from "../api/schema";
import type {
  ChartDatum,
  ChartSegment,
  ChartStackedDatum,
  ChartVariant,
} from "../ui/chartTypes";
import type { StatCardDelta } from "../ui/StatCard";
import {
  categoryFromGroups,
  dimensionValueLabel,
  formatPercent,
  formatUnits,
  orderedCategoryFromGroups,
  passRateOverTime,
  passRateByGroup,
  passRateScalar,
  segmentsFromGroups,
  seriesTotal,
  stackedByGroup,
  stackedOverTime,
  type MarkerDisplayNames,
  bucketedLine,
  type MetricBucket,
  type MetricDimension,
  type MetricName,
  type MetricsQueryResponse,
} from "./dashboardMetrics";

export type DashboardWidget = components["schemas"]["DashboardWidget"];
export type DashboardComparisonMode =
  components["schemas"]["DashboardComparisonMode"];
export type DashboardWidgetVisualization =
  components["schemas"]["DashboardWidgetVisualization"];
export type DashboardWidgetQuery =
  components["schemas"]["DashboardWidgetQuery"];
export type DashboardWidgetLayout =
  components["schemas"]["DashboardWidgetLayout"];
export type DashboardConfig = components["schemas"]["DashboardConfig"];
export type MetricFilters = components["schemas"]["MetricFilters"];

// --- visualization vocabulary ------------------------------------------------------

export const METRIC_VISUALIZATIONS: readonly DashboardWidgetVisualization[] = [
  "stat",
  "timeseries",
  "area",
  "bar",
  "stacked_bar",
  "donut",
  "top_list",
  "table",
];

export const VISUALIZATION_LABELS: Record<
  DashboardWidgetVisualization,
  string
> = {
  stat: "Stat",
  timeseries: "Timeseries",
  area: "Area",
  bar: "Bar",
  stacked_bar: "Stacked bar",
  donut: "Donut",
  top_list: "Top list",
  table: "Table",
};

// Visualization whose data follows the time axis (mirrors the server's auto-bucket
// rule); the rest resolve `bucket=auto` to a single scalar per group.
export const TIME_SHAPED_VISUALIZATIONS: ReadonlySet<DashboardWidgetVisualization> =
  new Set(["timeseries", "area", "stacked_bar"]);

const DAY_MS = 24 * 60 * 60 * 1000;

// Client mirror of the server's `bucket=auto` derivation (`:render`): time
// windows bucket by span for time-shaped visualization, everything else is a scalar.
export function bucketForWindow(
  visualization: DashboardWidgetVisualization,
  window: { start: string; end: string },
): MetricBucket {
  if (!TIME_SHAPED_VISUALIZATIONS.has(visualization)) {
    return "none";
  }
  const span =
    new Date(window.end).getTime() - new Date(window.start).getTime();
  if (span <= 2 * DAY_MS) {
    return "hour";
  }
  if (span <= 60 * DAY_MS) {
    return "day";
  }
  return "week";
}

// Default grid footprint for a freshly added widget of each visualization.
export function defaultLayoutSize(
  visualization: DashboardWidgetVisualization,
): {
  width: number;
  height: number;
} {
  if (visualization === "stat") {
    return { width: 3, height: 2 };
  }
  return { width: 6, height: 4 };
}

// --- metric registry mirror ----------------------------------------------

export type WidgetValueKind = "count" | "percent" | "units" | "ms" | "value";

export interface MetricMetadata {
  label: string;
  /** Valid `group_by` dimensions (docs/04 §11; `core` REGISTRY). */
  dimensions: readonly MetricDimension[];
  /** Valid typed filter keys. */
  filters: readonly (keyof MetricFilters)[];
  /** Dimensions that MUST be present in `group_by`. */
  requiredDimensions: readonly MetricDimension[];
  /** Pinned exact `group_by` (the regression axis) — editor locks it. */
  pinnedGroupBy?: readonly MetricDimension[];
  bucketable: boolean;
  measures: readonly { key: string; label: string }[];
  /** The measure a single-valued visualization (stat/bar/donut/top list) reads. */
  primaryMeasure: string;
  valueKind: WidgetValueKind;
  allowedVisualizations: readonly DashboardWidgetVisualization[];
}

// Mirrors `packages/core/src/marker/core/metrics.py:REGISTRY`. The server
// remains the validation authority (a drifted mirror surfaces as a
// VALIDATION_FAILED preview, never wrong data).
export const METRIC_METADATA: Record<MetricName, MetricMetadata> = {
  marker_pass_rate: {
    label: "Marker pass rate",
    dimensions: [
      "marker_id",
      "agent_id",
      "agent_version_id",
      "batch_id",
      "source",
    ],
    filters: [
      "marker_id",
      "agent_id",
      "agent_version_id",
      "batch_id",
      "source",
    ],
    requiredDimensions: [],
    bucketable: true,
    measures: [
      { key: "pass", label: "Pass" },
      { key: "warn", label: "Warn" },
      { key: "fail", label: "Fail" },
      { key: "pass_rate", label: "Pass rate" },
      { key: "warn_rate", label: "Warn rate" },
      { key: "fail_rate", label: "Fail rate" },
      { key: "eligible", label: "Eligible" },
      { key: "not_eligible", label: "Not eligible" },
      { key: "not_eligible_rate", label: "Not eligible rate" },
    ],
    primaryMeasure: "pass_rate",
    valueKind: "percent",
    allowedVisualizations: METRIC_VISUALIZATIONS,
  },
  marker_numeric_average: {
    label: "Marker numeric average",
    dimensions: [
      "marker_id",
      "agent_id",
      "agent_version_id",
      "batch_id",
      "source",
    ],
    filters: [
      "marker_id",
      "agent_id",
      "agent_version_id",
      "batch_id",
      "source",
    ],
    requiredDimensions: [],
    bucketable: true,
    measures: [
      { key: "average", label: "Average" },
      { key: "count", label: "Numeric answers" },
    ],
    primaryMeasure: "average",
    valueKind: "value",
    allowedVisualizations: [
      "stat",
      "timeseries",
      "area",
      "bar",
      "top_list",
      "table",
    ],
  },
  marker_category_distribution: {
    label: "Marker category distribution",
    dimensions: [
      "marker_id",
      "agent_id",
      "agent_version_id",
      "batch_id",
      "source",
      "answer_value",
    ],
    filters: [
      "marker_id",
      "agent_id",
      "agent_version_id",
      "batch_id",
      "source",
    ],
    requiredDimensions: ["marker_id", "answer_value"],
    bucketable: true,
    measures: [{ key: "count", label: "Answers" }],
    primaryMeasure: "count",
    valueKind: "count",
    allowedVisualizations: ["bar", "donut", "top_list", "table"],
  },
  transcript_volume: {
    label: "Transcript volume",
    dimensions: [
      "transcript_status",
      "fail_class",
      "source",
      "agent_id",
      "batch_id",
    ],
    filters: [
      "transcript_status",
      "fail_class",
      "source",
      "agent_id",
      "batch_id",
    ],
    requiredDimensions: [],
    bucketable: true,
    measures: [{ key: "count", label: "Transcripts" }],
    primaryMeasure: "count",
    valueKind: "count",
    allowedVisualizations: METRIC_VISUALIZATIONS,
  },
  eval_volume: {
    label: "Eval volume",
    dimensions: [
      "marker_id",
      "agent_id",
      "agent_version_id",
      "batch_id",
      "source",
    ],
    filters: [
      "marker_id",
      "agent_id",
      "agent_version_id",
      "batch_id",
      "source",
    ],
    requiredDimensions: [],
    bucketable: true,
    measures: [
      { key: "count", label: "Evals" },
      { key: "eval_failed", label: "Eval failed" },
      { key: "eval_failure_rate", label: "Eval failure rate" },
    ],
    primaryMeasure: "count",
    valueKind: "count",
    allowedVisualizations: METRIC_VISUALIZATIONS,
  },
  usage_units: {
    label: "Usage",
    dimensions: [
      "agent_id",
      "event_type",
      "modality",
      "transport",
      "initiator",
      "provider",
    ],
    filters: [
      "agent_id",
      "event_type",
      "modality",
      "transport",
      "initiator",
      "provider",
    ],
    requiredDimensions: ["event_type"],
    bucketable: true,
    measures: [
      { key: "quantity", label: "Quantity" },
      { key: "count", label: "Usage events" },
    ],
    primaryMeasure: "quantity",
    valueKind: "units",
    allowedVisualizations: METRIC_VISUALIZATIONS,
  },
  regression_delta: {
    label: "Version regression",
    dimensions: ["agent_version_id"],
    filters: ["agent_id", "marker_id"],
    requiredDimensions: ["agent_version_id"],
    pinnedGroupBy: ["agent_version_id"],
    bucketable: false,
    measures: [
      { key: "pass_rate", label: "Pass rate" },
      { key: "count", label: "Evaluations" },
      { key: "delta", label: "Delta vs previous version" },
    ],
    primaryMeasure: "pass_rate",
    valueKind: "percent",
    allowedVisualizations: ["bar", "top_list", "table"],
  },
  span_latency: {
    label: "Span latency",
    dimensions: ["name", "kind"],
    filters: ["name", "kind"],
    requiredDimensions: [],
    bucketable: true,
    measures: [
      { key: "count", label: "Spans" },
      { key: "avg_ms", label: "Average ms" },
      { key: "p95_ms", label: "P95 ms" },
    ],
    primaryMeasure: "p95_ms",
    valueKind: "ms",
    allowedVisualizations: [
      "stat",
      "timeseries",
      "area",
      "bar",
      "top_list",
      "table",
    ],
  },
};

export const METRIC_OPTIONS = Object.entries(METRIC_METADATA).map(
  ([metric, metricMetadata]) => ({
    value: metric as MetricName,
    label: metricMetadata.label,
  }),
);

export function formatWidgetValue(
  kind: WidgetValueKind,
  value: number,
): string {
  if (kind === "percent") {
    return formatPercent(value);
  }
  if (kind === "ms") {
    return `${Math.round(value).toLocaleString()}ms`;
  }
  if (kind === "value") {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return formatUnits(value);
}

// "Pass rate by marker", "Usage over time", "Transcripts".
export function defaultWidgetTitle(
  metric: MetricName,
  visualization: DashboardWidgetVisualization,
  groupBy: readonly MetricDimension[],
): string {
  const metricMetadata = METRIC_METADATA[metric];
  const grouped = groupBy.filter((dimension) => dimension !== "answer_value");
  if (grouped.length > 0) {
    const dimensionLabels = grouped
      .map((dimension) => dimension.replace(/_id$/, "").replaceAll("_", " "))
      .join(" and ");
    return `${metricMetadata.label} by ${dimensionLabels}`;
  }
  if (TIME_SHAPED_VISUALIZATIONS.has(visualization)) {
    return `${metricMetadata.label} over time`;
  }
  return metricMetadata.label;
}

export function newWidgetId(): string {
  return globalThis.crypto.randomUUID();
}

// --- rendering ------------------------------------------------------------

export interface WidgetTableRow {
  key: string;
  cells: string[];
}

export interface WidgetTable {
  headers: string[];
  rows: WidgetTableRow[];
}

export type RenderedWidgetContent =
  | { kind: "stat"; value: string; delta?: StatCardDelta }
  | {
      kind: "chart";
      variant: Exclude<ChartVariant, "stacked-bar" | "donut">;
      data: ChartDatum[];
      valueFormatter: (value: number) => string;
    }
  | {
      kind: "stacked";
      data: ChartStackedDatum[];
      valueFormatter: (value: number) => string;
    }
  | { kind: "donut"; data: ChartSegment[] }
  | { kind: "table"; table: WidgetTable };

function statScalar(
  metric: MetricName,
  response: MetricsQueryResponse,
): number | null {
  if (metric === "marker_pass_rate") {
    return passRateScalar(response);
  }
  const metricMetadata = METRIC_METADATA[metric];
  const hasPoints = (response.series ?? []).some((series) =>
    (series.groups ?? []).some((group) => (group.points ?? []).length > 0),
  );
  if (!hasPoints) {
    return null;
  }
  return seriesTotal(response, metricMetadata.primaryMeasure);
}

function statDelta(
  metric: MetricName,
  current: number | null,
  previousResponse: MetricsQueryResponse | undefined,
): StatCardDelta | undefined {
  if (!previousResponse || current === null) {
    return undefined;
  }
  const previous = statScalar(metric, previousResponse);
  if (previous === null) {
    return undefined;
  }
  const metricMetadata = METRIC_METADATA[metric];
  return {
    value: current - previous,
    period: "vs previous period",
    formatter: (magnitude) =>
      metricMetadata.valueKind === "percent"
        ? `${magnitude.toLocaleString(undefined, { maximumFractionDigits: 1 })} pts`
        : formatWidgetValue(metricMetadata.valueKind, magnitude),
  };
}

function lineData(
  metric: MetricName,
  response: MetricsQueryResponse,
  bucket: MetricBucket,
): ChartDatum[] {
  if (metric === "marker_pass_rate") {
    return passRateOverTime(response, bucket);
  }
  return bucketedLine(response, METRIC_METADATA[metric].primaryMeasure, bucket);
}

function tableData(
  metric: MetricName,
  response: MetricsQueryResponse,
  markerDisplayNames?: MarkerDisplayNames,
): WidgetTable {
  const metricMetadata = METRIC_METADATA[metric];
  const valueKind =
    metric === "marker_pass_rate" ? "count" : metricMetadata.valueKind;
  const groupBy = response.group_by ?? [];
  const headers = [
    ...groupBy.map((dimension) => dimension.replaceAll("_", " ")),
    ...metricMetadata.measures.map((measure) => measure.label),
  ];
  const rowMap = new Map<
    string,
    { labels: string[]; values: Map<string, number> }
  >();
  const order: string[] = [];
  for (const series of response.series ?? []) {
    for (const group of series.groups ?? []) {
      const dimensions = group.dimensions ?? [];
      const key =
        dimensions
          .map((coord) => `${coord.dimension}=${coord.value}`)
          .join("&") || "all";
      let row = rowMap.get(key);
      if (!row) {
        row = {
          labels:
            dimensions.length > 0
              ? dimensions.map((coord) =>
                  dimensionValueLabel(
                    coord.dimension,
                    coord.value,
                    markerDisplayNames,
                  ),
                )
              : groupBy.length > 0
                ? groupBy.map(() => "All")
                : ["All"],
          values: new Map(),
        };
        rowMap.set(key, row);
        order.push(key);
      }
      row.values.set(
        series.key,
        (group.points ?? []).reduce(
          (total, point) => total + (point.kind === "scalar" ? point.value : 0),
          0,
        ),
      );
    }
  }
  const effectiveHeaders =
    groupBy.length > 0
      ? headers
      : ["Scope", ...metricMetadata.measures.map((measure) => measure.label)];
  return {
    headers: effectiveHeaders,
    rows: order.map((key) => {
      const row = rowMap.get(key);
      return {
        key,
        cells: [
          ...(row?.labels ?? []),
          ...metricMetadata.measures.map((measure) => {
            const value = row?.values.get(measure.key) ?? 0;
            return measure.key.endsWith("_rate")
              ? formatPercent(value * 100)
              : formatWidgetValue(valueKind, value);
          }),
        ],
      };
    }),
  };
}

// The single (metric, visualization, response) -> renderable transform every tile uses.
export function renderWidgetContent(
  widget: Pick<DashboardWidget, "visualization"> & {
    query?: { metric: MetricName } | null;
  },
  response: MetricsQueryResponse,
  previousResponse?: MetricsQueryResponse,
  markerDisplayNames?: MarkerDisplayNames,
): RenderedWidgetContent {
  const metric = widget.query?.metric ?? "transcript_volume";
  const metricMetadata = METRIC_METADATA[metric];
  const bucket = response.bucket;
  const formatter = (value: number) =>
    formatWidgetValue(metricMetadata.valueKind, value);
  const countFormatter = (value: number) => formatWidgetValue("count", value);

  switch (widget.visualization) {
    case "stat": {
      const value = statScalar(metric, response);
      return {
        kind: "stat",
        value:
          value === null
            ? "--"
            : formatWidgetValue(metricMetadata.valueKind, value),
        delta: statDelta(metric, value, previousResponse),
      };
    }
    case "timeseries":
      return {
        kind: "chart",
        variant: "line",
        data: lineData(metric, response, bucket),
        valueFormatter:
          metric === "marker_pass_rate" ? formatPercent : formatter,
      };
    case "area":
      return {
        kind: "chart",
        variant: "area",
        data: lineData(metric, response, bucket),
        valueFormatter:
          metric === "marker_pass_rate" ? formatPercent : formatter,
      };
    case "bar": {
      const data =
        metric === "marker_pass_rate"
          ? passRateByGroup(response, false, markerDisplayNames)
          : metric === "regression_delta"
            ? orderedCategoryFromGroups(
                response,
                metricMetadata.primaryMeasure,
                markerDisplayNames,
              )
            : categoryFromGroups(
                response,
                metricMetadata.primaryMeasure,
                markerDisplayNames,
              );
      return { kind: "chart", variant: "bar", data, valueFormatter: formatter };
    }
    case "top_list":
      return {
        kind: "chart",
        variant: "top-list",
        data:
          metric === "marker_pass_rate"
            ? passRateByGroup(response, false, markerDisplayNames)
            : metric === "regression_delta"
              ? orderedCategoryFromGroups(
                  response,
                  metricMetadata.primaryMeasure,
                  markerDisplayNames,
                )
              : categoryFromGroups(
                  response,
                  metricMetadata.primaryMeasure,
                  markerDisplayNames,
                ),
        valueFormatter: formatter,
      };
    case "donut":
      return {
        kind: "donut",
        data:
          metric === "marker_pass_rate"
            ? passRateByGroup(response, false, markerDisplayNames)
            : segmentsFromGroups(
                response,
                metricMetadata.primaryMeasure,
                markerDisplayNames,
              ),
      };
    case "stacked_bar": {
      // Bucketed: rows per bucket, segments per group. Scalar: rows per
      // group, segments per measure (pass/warn/fail by version).
      const data =
        bucket !== "none"
          ? stackedOverTime(
              response,
              metricMetadata.primaryMeasure,
              bucket,
              markerDisplayNames,
            )
          : stackedByGroup(
              response,
              metric === "marker_pass_rate"
                ? metricMetadata.measures.slice(0, 3)
                : metricMetadata.measures,
              markerDisplayNames,
            );
      return {
        kind: "stacked",
        data,
        valueFormatter:
          metric === "marker_pass_rate" ? countFormatter : formatter,
      };
    }
    case "table":
      return {
        kind: "table",
        table: tableData(metric, response, markerDisplayNames),
      };
  }
}

export function isWidgetContentEmpty(content: RenderedWidgetContent): boolean {
  switch (content.kind) {
    case "stat":
      return content.value === "--";
    case "chart":
    case "stacked":
    case "donut":
      return content.data.length === 0;
    case "table":
      return content.table.rows.length === 0;
  }
}

// --- starter template ------------------------------------------------------

export interface DashboardTemplate {
  id: string;
  label: string;
  widgets: () => DashboardWidget[];
}

function templateWidget(
  title: string,
  visualization: DashboardWidgetVisualization,
  layout: DashboardWidgetLayout,
  query: DashboardWidgetQuery,
  comparison: DashboardComparisonMode | null = null,
): DashboardWidget {
  return {
    id: newWidgetId(),
    title,
    visualization,
    query,
    layout,
    comparison,
  };
}

function metricQuery(
  metric: MetricName,
  groupBy: MetricDimension[] = [],
  filters: Partial<MetricFilters> = {},
  bucket: MetricBucket | null = null,
): DashboardWidgetQuery {
  return {
    metric,
    group_by: groupBy,
    filters: filters as MetricFilters,
    bucket,
  };
}

export const OVERVIEW_TEMPLATE: DashboardTemplate = {
  id: "overview",
  label: "Overview",
  widgets: () => [
    templateWidget(
      "Pass rate",
      "stat",
      { x: 0, y: 0, width: 3, height: 2 },
      metricQuery("marker_pass_rate"),
      "previous_period",
    ),
    templateWidget(
      "Evaluations",
      "stat",
      { x: 3, y: 0, width: 3, height: 2 },
      metricQuery("eval_volume"),
      "previous_period",
    ),
    templateWidget(
      "Transcripts",
      "stat",
      { x: 6, y: 0, width: 3, height: 2 },
      metricQuery("transcript_volume"),
      "previous_period",
    ),
    templateWidget(
      "Pass rate over time",
      "timeseries",
      { x: 0, y: 2, width: 6, height: 4 },
      metricQuery("marker_pass_rate"),
    ),
    templateWidget(
      "Pass / warn / fail by version",
      "stacked_bar",
      { x: 6, y: 2, width: 6, height: 4 },
      metricQuery("marker_pass_rate", ["agent_version_id"], {}, "none"),
    ),
    templateWidget(
      "Transcript volume over time",
      "area",
      { x: 0, y: 6, width: 6, height: 4 },
      metricQuery("transcript_volume"),
    ),
    templateWidget(
      "Failure class",
      "donut",
      { x: 6, y: 6, width: 6, height: 4 },
      metricQuery("transcript_volume", ["fail_class"], {
        transcript_status: "failed",
      }),
    ),
    templateWidget(
      "Marker firing counts",
      "top_list",
      { x: 0, y: 10, width: 6, height: 4 },
      metricQuery("eval_volume", ["marker_id"]),
    ),
    templateWidget(
      "Usage by unit type",
      "bar",
      { x: 6, y: 10, width: 6, height: 4 },
      metricQuery("usage_units", ["event_type"]),
    ),
  ],
};

export const DASHBOARD_TEMPLATES: readonly DashboardTemplate[] = [
  OVERVIEW_TEMPLATE,
];
